/**
 * Kleine bouwstenen van de Notities-pagina: eigenschap-rij met zoekbare kiezer
 * en de meeting-kiezer (FF Recorder). Module-niveau, zodat niets remount per toets.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, ButtonBase, CircularProgress, Dialog, DialogContent, InputBase, List, ListItemButton, ListItemText, Popover, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';

export const INKT = '#1f2937';
export const STIL = '#8a94a3';
export const LIJN = '#eceef1';
export const ACCENT = '#526075';

/**
 * Duidelijke, rustige link naar de projectpagina (wiki-feedback Notities: je wilt vanuit
 * een notitie naar het project om daar dingen aan te vullen). Opent de projectpagina zelf,
 * niet het notitiepaneel. navigate komt van de ouder (geen router-hook in dit bestand).
 */
export function NaarProjectKnop({ projectId, navigate }) {
  if (!projectId) return null;
  return (
    <ButtonBase onClick={() => navigate(`/projects/${projectId}`)} aria-label="Naar de projectpagina"
      sx={{ ml: 1, gap: 0.5, px: 1, py: 0.35, borderRadius: 1, border: `1px solid ${LIJN}`, flexShrink: 0,
        fontSize: 12.5, color: ACCENT, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#f3f4f6', color: INKT } }}>
      Naar projectpagina
      <ArrowForwardIcon sx={{ fontSize: 14 }} />
    </ButtonBase>
  );
}

/** Eén stille eigenschap-rij zoals in Notion: label links, waarde rechts, klik = kiezen. */
export function EigenschapRij({ label, waarde, leeg = 'Leeg', onClick, extra }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
      <Typography sx={{ width: 110, flexShrink: 0, fontSize: 13.5, color: STIL }}>{label}</Typography>
      <ButtonBase onClick={onClick} disabled={!onClick}
        sx={{ justifyContent: 'flex-start', px: 0.75, py: 0.4, ml: -0.75, borderRadius: 1, maxWidth: '100%', minWidth: 0,
          '&:hover': { bgcolor: '#f3f4f6' } }}>
        <Typography noWrap sx={{ fontSize: 13.5, color: waarde ? INKT : '#b6bec9' }}>{waarde || leeg}</Typography>
      </ButtonBase>
      {extra}
    </Box>
  );
}

/** Zoekbare lijst in een popover. opties: [{ key, label, sub, waarde }]. */
export function KiesPopover({ anchorEl, onClose, opties, onKies, placeholder = 'Zoek…' }) {
  const [q, setQ] = useState('');
  useEffect(() => { if (anchorEl) setQ(''); }, [anchorEl]);
  const hits = useMemo(() => {
    const woorden = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const lijst = woorden.length
      ? opties.filter((o) => woorden.every((w) => `${o.label} ${o.sub || ''}`.toLowerCase().includes(w)))
      : opties;
    return lijst.slice(0, 50);
  }, [q, opties]);
  return (
    <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ elevation: 0, sx: { width: 340, mt: 0.5, borderRadius: 2, border: `1px solid ${LIJN}`, boxShadow: '0 8px 24px rgba(15,23,42,0.10)' } }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${LIJN}` }}>
        <InputBase autoFocus fullWidth value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === 'Enter' && hits[0]) onKies(hits[0]); }}
          sx={{ fontSize: 14 }} />
      </Box>
      <List dense disablePadding sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
        {hits.length === 0 && <Typography sx={{ fontSize: 13, color: STIL, px: 1.5, py: 1 }}>Niets gevonden</Typography>}
        {hits.map((o) => (
          <ListItemButton key={o.key} onClick={() => onKies(o)} sx={{ py: 0.5 }}>
            <ListItemText primary={o.label} secondary={o.sub}
              primaryTypographyProps={{ sx: { fontSize: 13.5, color: INKT } }}
              secondaryTypographyProps={{ sx: { fontSize: 12, color: STIL } }} />
          </ListItemButton>
        ))}
      </List>
    </Popover>
  );
}

/** Kiezer voor een FF Recorder-meeting: zoeken op titel, meetings van het project bovenaan. */
export function MeetingKoppelDialog({ open, onClose, projectId, onKies }) {
  const [q, setQ] = useState('');
  const [lijst, setLijst] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let dood = false;
    setLijst(null);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: q.trim() });
        if (projectId) params.set('projectId', projectId);
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/meetings?${params}`);
        const d = await leesJson(r, 'Meetings zoeken');
        if (!dood) setLijst(d.meetings || []);
      } catch (e) { if (!dood) { meldFout(e, 'Meetings zoeken'); setLijst([]); } }
    }, 250);
    return () => { dood = true; clearTimeout(t); };
  }, [open, q, projectId]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <InputBase autoFocus fullWidth value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek een meeting op titel…" sx={{ fontSize: 15 }} />
      </Box>
      <DialogContent sx={{ pt: 0 }}>
        {lijst === null ? <CircularProgress size={18} sx={{ m: 1 }} /> : lijst.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: STIL, py: 2 }}>Geen meetings gevonden (alleen meetings waar je bij was of host van bent).</Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {lijst.map((m) => (
              <ListItemButton key={m.id} onClick={() => onKies(m)} sx={{ borderRadius: 1, alignItems: 'flex-start' }}>
                <ListItemText
                  primary={`${m.titel}${m.gekoppeld ? '  · bij dit project' : ''}`}
                  secondary={`${m.datum ? new Date(m.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}${m.duur ? ` · ${m.duur} min` : ''}${m.samenvatting ? ` · ${m.samenvatting.slice(0, 110)}…` : ' · nog geen samenvatting'}`}
                  primaryTypographyProps={{ sx: { fontSize: 13.5, color: INKT, fontWeight: 600 } }}
                  secondaryTypographyProps={{ sx: { fontSize: 12, color: STIL } }} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
